import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';

import useTranslations from '@/i18n/useTranslations';
import Text from '@/shared-components/text/components/Text';

import BoardVirtualList from './BoardVirtualList';

import './styles/ArticlesVirtualList.css';

function ArticlesVirtualList(
  { items, publishedItems, queuedItems = [], onLoadMore, cardVariant = 'default' },
) {
  const t = useTranslations('common.profile.pages.board');
  const autoLoadCheckpointRef = useRef(0);

  const displayItems = useMemo(() => publishedItems ?? [], [publishedItems]);
  const hasQueued = queuedItems.length > 0;
  const shouldLoadMore = items.data.length < items.totalDocs;

  const emptyButQueued = displayItems.length === 0 && hasQueued;

  // Auto-fetch the next page when the current batch only contains queued items.
  useEffect(() => {
    if (displayItems.length === 0 && shouldLoadMore && items.data.length > autoLoadCheckpointRef.current) {
      autoLoadCheckpointRef.current = items.data.length;
      onLoadMore();
    }
    if (!shouldLoadMore) {
      autoLoadCheckpointRef.current = 0;
    }
  }, [displayItems.length, shouldLoadMore, items.data.length, onLoadMore]);

  return (
    <>
      {emptyButQueued && (
        // Reassure the user when everything is scheduled but nothing is live yet.
        (<Text className="articles-queued__empty-hint font-size-small" paragraph>
          {t('empty.articles_queued_internal')}
        </Text>)
      )}
      <BoardVirtualList
        items={{ ...items, data: displayItems }}
        onLoadMore={onLoadMore}
        cardVariant={cardVariant}
        gridVariant={cardVariant === 'board' ? 'board' : ''}
        shouldLoadMore={shouldLoadMore}
        listLength={displayItems.length}
      />
    </>
  );
}

ArticlesVirtualList.propTypes = {
  items: PropTypes.shape({
    data: PropTypes.array.isRequired,
    totalDocs: PropTypes.number.isRequired
  }).isRequired,
  publishedItems: PropTypes.array.isRequired,
  queuedItems: PropTypes.array,
  onLoadMore: PropTypes.func.isRequired,
  cardVariant: PropTypes.oneOf(['default', 'board'])
};
export default ArticlesVirtualList;
